import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, Check, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useLanguage } from '../context/LanguageContext';
import { createTransaction } from '../lib/api';
import { toast } from 'sonner';

const COLUMN_MAPPINGS = [
  { id: 'date', label_fr: 'Date', label_en: 'Date', label_pt: 'Data', required: true },
  { id: 'amount', label_fr: 'Montant', label_en: 'Amount', label_pt: 'Montante', required: true },
  { id: 'description', label_fr: 'Description', label_en: 'Description', label_pt: 'Descrição', required: false },
  { id: 'category', label_fr: 'Catégorie', label_en: 'Category', label_pt: 'Categoria', required: false },
];

export default function ImportCSVModal({ open, onClose, onSuccess }) {
  const { language, t } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState('upload'); // upload, mapping, preview, importing
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState({ success: 0, failed: 0 });

  const parseCSV = (text) => {
    try {
      // Normalize line endings
      const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = normalizedText.split('\n').filter(line => line.trim());
      
      console.log('[CSV Parse] Total lines:', lines.length);
      console.log('[CSV Parse] First line sample:', lines[0]?.substring(0, 100));
      
      if (lines.length < 1) {
        console.warn('[CSV Parse] File is empty');
        return { headers: [], data: [] };
      }
      
      // Smart separator detection - count occurrences in first few lines
      const detectSeparator = (sampleLines) => {
        const separators = [
          { char: '\t', name: 'tab' },
          { char: ';', name: 'semicolon' },
          { char: ',', name: 'comma' },
          { char: '|', name: 'pipe' }
        ];
        
        let bestSeparator = ',';
        let maxScore = 0;
        
        for (const sep of separators) {
          // Count occurrences in each line
          const counts = sampleLines.slice(0, Math.min(5, sampleLines.length)).map(line => 
            (line.match(new RegExp(sep.char === '\t' ? '\t' : '\\' + sep.char, 'g')) || []).length
          );
          
          // Check if counts are consistent (good indicator of correct separator)
          const avgCount = counts.reduce((a, b) => a + b, 0) / counts.length;
          const isConsistent = counts.every(c => Math.abs(c - avgCount) <= 1);
          
          const score = avgCount * (isConsistent ? 2 : 1);
          
          console.log(`[CSV Parse] Separator '${sep.name}': avg=${avgCount.toFixed(1)}, consistent=${isConsistent}, score=${score.toFixed(1)}`);
          
          if (score > maxScore && avgCount >= 1) {
            maxScore = score;
            bestSeparator = sep.char;
          }
        }
        
        return bestSeparator;
      };
      
      const separator = detectSeparator(lines);
      console.log('[CSV Parse] Selected separator:', separator === '\t' ? 'TAB' : separator);
      
      // Parse with quotes handling
      const parseLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"' && !inQuotes) {
            inQuotes = true;
          } else if (char === '"' && inQuotes) {
            if (line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else if (char === separator && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        
        return result;
      };
      
      // Parse headers
      const headers = parseLine(lines[0]).map(h => h.replace(/^["']|["']$/g, '').trim()).filter(h => h);
      console.log('[CSV Parse] Headers found:', headers);
      
      if (headers.length === 0) {
        console.warn('[CSV Parse] No headers found');
        return { headers: [], data: [] };
      }
      
      // Parse data rows
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = parseLine(lines[i]);
        const row = {};
        
        headers.forEach((h, idx) => {
          row[h] = (values[idx] || '').replace(/^["']|["']$/g, '').trim();
        });
        
        // Skip empty rows
        if (Object.values(row).some(v => v)) {
          data.push(row);
        }
      }
      
      console.log('[CSV Parse] Data rows parsed:', data.length);
      if (data.length > 0) {
        console.log('[CSV Parse] First row sample:', JSON.stringify(data[0]));
      }
      
      return { headers, data };
    } catch (error) {
      console.error('[CSV Parse] Error:', error);
      return { headers: [], data: [] };
    }
  };

  const handleFileChange = async (e) => {
    console.log('[CSV] File input changed');
    const file = e.target.files[0];
    if (!file) {
      console.log('[CSV] No file selected');
      return;
    }
    console.log('[CSV] File selected:', file.name, file.type, file.size);
    
    // Validate file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.txt')) {
      console.log('[CSV] Invalid extension:', fileName);
      toast.error(t('invalid_csv_format') || 'Format invalide. Utilisez un fichier CSV ou TXT.');
      e.target.value = ''; // Reset input
      return;
    }
    
    try {
      console.log('[CSV] Reading file...');
      setFile(file);
      
      const text = await file.text();
      console.log('[CSV] File content length:', text.length);
      console.log('[CSV] First 200 chars:', text.substring(0, 200));
      
      const { headers, data } = parseCSV(text);
      console.log('[CSV] Parse result - headers:', headers.length, 'data:', data.length);
      
      if (headers.length === 0) {
        console.warn('[CSV] No headers found');
        toast.error(t('empty_csv') || 'Le fichier CSV est vide ou mal formaté. Vérifiez que le fichier contient des en-têtes de colonnes.');
        e.target.value = '';
        setFile(null);
        return;
      }
      
      if (data.length === 0) {
        console.warn('[CSV] No data rows found');
        toast.error(t('csv_no_data') || 'Le fichier CSV ne contient pas de données (seulement des en-têtes).');
        e.target.value = '';
        setFile(null);
        return;
      }
      
      console.log('[CSV] Setting headers and data...');
      setHeaders(headers);
      setCsvData(data);
      
      // Auto-detect column mapping
      console.log('[CSV] Auto-detecting columns...');
      const autoMapping = {};
      headers.forEach(h => {
        const hLower = h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (hLower.includes('date') || hLower.includes('data')) {
          autoMapping.date = h;
        } else if (hLower.includes('montant') || hLower.includes('amount') || hLower.includes('debit') || hLower.includes('credit') || hLower.includes('valor') || hLower.includes('valeur')) {
          autoMapping.amount = h;
        } else if (hLower.includes('description') || hLower.includes('libelle') || hLower.includes('label') || hLower.includes('descricao') || hLower.includes('motif') || hLower.includes('operation')) {
          autoMapping.description = h;
        } else if (hLower.includes('categorie') || hLower.includes('category') || hLower.includes('categoria')) {
          autoMapping.category = h;
        }
      });
      console.log('[CSV] Auto-mapping:', autoMapping);
      setColumnMapping(autoMapping);
      
      console.log('[CSV] Changing step to mapping...');
      setStep('mapping');
      console.log('[CSV] Step changed successfully');
    } catch (error) {
      console.error('[CSV] Error processing file:', error);
      toast.error((t('csv_parse_error') || 'Erreur lors de la lecture du fichier CSV.') + ' ' + error.message);
      e.target.value = '';
      setFile(null);
    }
  };

  const handleImport = async () => {
    setStep('importing');
    setImportProgress(0);
    setImportResults({ success: 0, failed: 0 });
    
    const results = { success: 0, failed: 0 };
    
    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      
      try {
        // Parse amount (handle French format: 1 234,56)
        let amount = row[columnMapping.amount] || '0';
        amount = amount.replace(/\s/g, '').replace(',', '.');
        amount = parseFloat(amount);
        if (isNaN(amount)) amount = 0;
        amount = Math.abs(amount); // Make positive
        
        // Parse date
        let date = row[columnMapping.date] || new Date().toISOString();
        // Try to parse common date formats
        const dateFormats = [
          /^(\d{2})\/(\d{2})\/(\d{4})$/, // DD/MM/YYYY
          /^(\d{4})-(\d{2})-(\d{2})$/, // YYYY-MM-DD
          /^(\d{2})-(\d{2})-(\d{4})$/, // DD-MM-YYYY
        ];
        for (const fmt of dateFormats) {
          const match = date.match(fmt);
          if (match) {
            if (fmt === dateFormats[0]) {
              date = `${match[3]}-${match[2]}-${match[1]}`;
            } else if (fmt === dateFormats[2]) {
              date = `${match[3]}-${match[2]}-${match[1]}`;
            }
            break;
          }
        }
        
        const description = row[columnMapping.description] || 'Import CSV';
        const category = row[columnMapping.category]?.toLowerCase() || 'divers';
        
        if (amount > 0) {
          const response = await createTransaction({
            amount,
            date,
            description,
            category,
            payment_method: 'card'
          });
          
          // Check for transaction limit warning (only show once, on the last imported)
          if (response.data?.warning && i === csvData.length - 1) {
            const warning = response.data.warning;
            const message = language === 'fr' ? warning.message_fr : language === 'pt' ? (warning.message_pt || warning.message_en) : warning.message_en;
            
            if (warning.type === 'transaction_limit_reached') {
              toast.error(message, { duration: 6000 });
            } else if (warning.type === 'transaction_limit_approaching') {
              toast.warning(message, { duration: 5000 });
            }
          }
          
          results.success++;
        }
      } catch (error) {
        // Check if it's a limit error
        if (error?.response?.status === 403) {
          toast.error(
            language === 'fr' 
              ? 'Limite de transactions atteinte ! Import arrêté.'
              : language === 'pt'
              ? 'Limite de transações atingido! Importação parada.'
              : 'Transaction limit reached! Import stopped.',
            { duration: 6000 }
          );
          break; // Stop importing
        }
        console.error('Failed to import row:', error);
        results.failed++;
      }
      
      setImportProgress(Math.round(((i + 1) / csvData.length) * 100));
      setImportResults({ ...results });
    }
    
    setStep('done');
    
    if (results.success > 0) {
      toast.success(t('transactions_imported').replace('{count}', results.success));
      onSuccess?.();
    }
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setColumnMapping({});
    setImportProgress(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            {t('import_csv_file')}
          </DialogTitle>
          <DialogDescription>
            {t('import_csv_desc')}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="py-8">
            <input
              id="csv-file-input"
              ref={fileInputRef}
              type="file"
              accept="*/*"
              onChange={handleFileChange}
              className="sr-only"
              data-testid="csv-file-input"
            />
            <label 
              htmlFor="csv-file-input"
              className="block w-full border-2 border-dashed border-muted-foreground/30 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              data-testid="csv-upload-zone"
            >
              <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="font-medium mb-1">
                {t('click_to_choose_file')}
              </p>
              <p className="text-sm text-muted-foreground">
                CSV, TXT (max 5MB)
              </p>
            </label>
            
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                💡 {t('export_csv_tip')}
              </p>
            </div>
          </div>
        )}

        {step === 'mapping' && (
          <div className="py-4 space-y-4">
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm">
                {csvData.length} {t('rows_detected')}
              </span>
            </div>
            
            <p className="text-sm font-medium">
              {t('map_columns')}
            </p>
            
            <div className="space-y-3">
              {COLUMN_MAPPINGS.map(col => (
                <div key={col.id} className="flex items-center gap-3">
                  <span className="w-28 text-sm">
                    {language === 'fr' ? col.label_fr : language === 'pt' ? col.label_pt : col.label_en}
                    {col.required && <span className="text-red-500">*</span>}
                  </span>
                  <Select
                    value={columnMapping[col.id] || 'none'}
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, [col.id]: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t('select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- {t('none') || 'Aucun'} --</SelectItem>
                      {headers.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            
            {/* Preview */}
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">
                {t('preview_first_rows')}
              </p>
              <div className="bg-muted/50 rounded-lg p-3 text-xs overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left p-1">Date</th>
                      <th className="text-left p-1">Montant</th>
                      <th className="text-left p-1">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 3).map((row, i) => (
                      <tr key={i}>
                        <td className="p-1">{row[columnMapping.date] || '-'}</td>
                        <td className="p-1">{row[columnMapping.amount] || '-'}</td>
                        <td className="p-1 truncate max-w-32">{row[columnMapping.description] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="font-medium mb-2">
              {t('importing')}
            </p>
            <div className="w-full bg-muted rounded-full h-2 mb-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${importProgress}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {importResults.success} ✓ / {importResults.failed} ✗
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <p className="font-bold text-xl mb-2">
              {t('import_complete')}
            </p>
            <div className="flex justify-center gap-4 text-sm">
              <span className="text-green-600">✓ {importResults.success} {t('success_label')}</span>
              {importResults.failed > 0 && (
                <span className="text-red-500">✗ {importResults.failed} {t('failed')}</span>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'mapping' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleImport}
                disabled={!columnMapping.date || !columnMapping.amount}
              >
                {t('import_rows').replace('{count}', csvData.length)}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose}>
              {t('close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
